// src/hooks/useTarjetaVecino.js
import { useState, useCallback, useRef } from 'react';
import TarjetaVecino from '../service/tarjetaVecino';
import { useNotify } from '../providers/NotificationProvider';

export const useTarjetaVecino = () => {
  const [validando, setValidando] = useState(false);
  const [estadoVecino, setEstadoVecino] = useState(null);
  const [error, setError] = useState(null);
  const notify = useNotify();
  // Token de secuencia: si el usuario cambia de familiar/RUT rápido, descartamos
  // las respuestas de validaciones anteriores (evita que una respuesta vieja gane).
  const seqRef = useRef(0);

  const validarRut = useCallback(async (rut) => {
    if (!rut) {
      notify.warning('Ingrese un RUT para validar');
      return null;
    }

    const seq = ++seqRef.current;
    try {
      setValidando(true);
      setError(null);
      const res = await TarjetaVecino.Validar(rut);
      if (seq !== seqRef.current) return null; // respuesta obsoleta: descartar

      if (res) {
        setEstadoVecino(res);

        // Notificaciones según el estado
        if (res.esVecino) {
          notify.success(
            '¡Tarjeta vecino vigente! Se aplicará descuento especial',
            { duration: 5 }
          );
        } else if (res.noVigente) {
          notify.warning('Tarjeta vecino no vigente');
        } else if (res.noExiste) {
          notify.info('RUT no registrado como vecino');
        }

        return res;
      }

      return null;
    } catch (err) {
      if (seq !== seqRef.current) return null; // error de una validación obsoleta
      console.error('Error validando tarjeta vecino:', err);
      // Fail-open: si no pudimos verificar la TV (API caída/timeout), NO bloqueamos
      // la inscripción. Avisamos y dejamos continuar sin el descuento; el backend
      // re-valida al crear la orden y puede aplicarlo si la TV se recupera.
      notify.warning('No pudimos verificar la Tarjeta Vecino. Puedes continuar; se cobrará sin el descuento vecino.');
      setEstadoVecino(null);
      setError(err);
      return null;
    } finally {
      if (seq === seqRef.current) setValidando(false);
    }
  }, [notify]);

  const resetEstado = useCallback(() => {
    seqRef.current++; // invalida cualquier respuesta en vuelo
    setEstadoVecino(null);
    setError(null);
  }, []);

  return {
    validarRut,
    validando,
    estadoVecino,
    error,
    esVecino: estadoVecino?.esVecino || false,
    resetEstado
  };
};
